import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrder, type Order } from '../../../utils/firebase';
import { ArrowLeft, Package, Mail, MapPin, CreditCard } from 'lucide-react';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function AdminOrderDetail() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!orderId) return;
    getOrder(orderId).then(data => {
      setOrder(data);
      setLoading(false);
    });
  }, [orderId]);
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-287">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-288" />
      </div>;
  }
  if (!order) {
    return <div className="p-8 text-center" data-id="element-289">
        <p className="text-[#5A5A5A]" data-id="element-290">Order not found.</p>
        <Link to="/admin/orders" className="text-tealslate hover:underline mt-4 inline-block" data-id="element-291">
          Back to Orders
        </Link>
      </div>;
  }
  const hasPhysical = order.lineItems.some(i => i.type === 'supplement');
  const hasDigital = order.lineItems.some(i => i.type === 'book');
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-292">
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm font-medium text-[#5A5A5A] hover:text-[#111111] mb-6 transition-colors" data-id="element-293">
        <ArrowLeft className="w-4 h-4" data-id="element-294" />
        Back to Orders
      </Link>

      <div className="flex items-center justify-between mb-8" data-id="element-295">
        <div data-id="element-296">
          <h1 className="text-2xl font-bold text-[#111111] mb-1" data-id="element-297">
            Order #{order.id.slice(0, 8).toUpperCase()}
          </h1>
          <p className="text-sm text-[#5A5A5A]" data-id="element-298">
            {formatDate(order.createdAt)}
          </p>
        </div>
        <div className="flex gap-2" data-id="element-299">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
            ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-300">
            {order.status}
          </span>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
            ${order.fulfillmentStatus === 'pending' ? 'bg-amber-100 text-amber-800' : order.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-301">
            {order.fulfillmentStatus.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-302">
        {/* Customer Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-303">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-304">
            <Mail className="w-5 h-5 text-[#9A9A90]" data-id="element-305" />
            <h2 className="font-bold" data-id="element-306">Customer</h2>
          </div>
          <p className="text-sm font-medium text-[#111111] mb-1" data-id="element-307">
            {order.shippingAddress?.recipientName || 'Digital Customer'}
          </p>
          <p className="text-sm text-[#5A5A5A] break-all" data-id="element-308">{order.email}</p>
        </div>

        {/* Shipping Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-309">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-310">
            <MapPin className="w-5 h-5 text-[#9A9A90]" data-id="element-311" />
            <h2 className="font-bold" data-id="element-312">Shipping</h2>
          </div>
          {!hasPhysical ? <p className="text-sm text-[#5A5A5A] italic" data-id="element-313">
              Digital Product - No shipping required.
            </p> : order.shippingAddress ? <div className="text-sm text-[#5A5A5A] space-y-1" data-id="element-314">
              <p className="font-medium text-[#111111]" data-id="element-315">
                {order.shippingAddress.recipientName}
              </p>
              <p data-id="element-316">{order.shippingAddress.line1}</p>
              {order.shippingAddress.line2 && <p data-id="element-317">{order.shippingAddress.line2}</p>}
              <p data-id="element-318">
                {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
                {order.shippingAddress.postalCode}
              </p>
              <p data-id="element-319">{order.shippingAddress.country}</p>
              {order.trackingNumber && <p className="mt-3 pt-3 border-t border-[#E5E5E5]" data-id="element-320">
                  <span className="font-medium text-[#111111]" data-id="element-321">Tracking:</span>{' '}
                  {order.trackingNumber}
                </p>}
            </div> : <p className="text-sm text-[#5A5A5A]" data-id="element-322">
              No shipping address provided.
            </p>}
        </div>

        {/* Payment Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-323">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-324">
            <CreditCard className="w-5 h-5 text-[#9A9A90]" data-id="element-325" />
            <h2 className="font-bold" data-id="element-326">Payment</h2>
          </div>
          <p className="text-sm text-[#5A5A5A] mb-1" data-id="element-327">
            Status:{' '}
            <span className="font-medium text-[#111111] capitalize" data-id="element-328">
              {order.status}
            </span>
          </p>
          <p className="text-sm text-[#5A5A5A] break-all" data-id="element-329">
            Session: {order.stripeSessionId}
          </p>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden mb-8" data-id="element-330">
        <div className="p-4 border-b border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-331">
          <h2 className="font-bold text-[#111111]" data-id="element-332">Products Ordered</h2>
        </div>
        <table className="w-full text-left border-collapse" data-id="element-333">
          <thead data-id="element-334">
            <tr className="border-b border-[#E5E5E5]" data-id="element-335">
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-336">
                Item
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-337">
                Price
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-338">
                Qty
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-339">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-340">
            {order.lineItems.map((item, i) => <tr key={i} data-id="element-341">
                <td className="px-6 py-4" data-id="element-342">
                  <p className="text-sm font-medium text-[#111111]" data-id="element-343">
                    {item.name}
                  </p>
                  <p className="text-xs text-[#5A5A5A] capitalize" data-id="element-344">
                    {item.type}
                  </p>
                </td>
                <td className="px-6 py-4 text-sm text-[#5A5A5A] text-right" data-id="element-345">
                  {formatPrice(item.unitPrice)}
                </td>
                <td className="px-6 py-4 text-sm text-[#5A5A5A] text-right" data-id="element-346">
                  {item.quantity}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-[#111111] text-right" data-id="element-347">
                  {formatPrice(item.unitPrice * item.quantity)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Order Summary */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm p-6 ml-auto w-full md:w-80" data-id="element-348">
        <h2 className="font-bold text-[#111111] mb-4" data-id="element-349">Order Summary</h2>
        <div className="space-y-3 text-sm" data-id="element-350">
          <div className="flex justify-between text-[#5A5A5A]" data-id="element-351">
            <span data-id="element-352">Subtotal</span>
            <span data-id="element-353">{formatPrice(order.subtotal)}</span>
          </div>
          {hasPhysical && <div className="flex justify-between text-[#5A5A5A]" data-id="element-354">
              <span data-id="element-355">Shipping</span>
              <span data-id="element-356">{formatPrice(order.total - order.subtotal)}</span>{' '}
              {/* Simplified for prototype */}
            </div>}
          <div className="pt-3 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111] text-base" data-id="element-357">
            <span data-id="element-358">Grand Total</span>
            <span data-id="element-359">{formatPrice(order.total)}</span>
          </div>
        </div>
      </div>
    </div>;
}